import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AumicoCoreProvider, NotificationType } from '@modeso/aumico-lib-core-fe';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { EMPTY, Observable, forkJoin, of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { integrationActions } from '../actions/integration.actions';
import {
    ConnectionMappingVendor,
    IBulkUpsertMappingResult,
} from '../models/integration/connection-mapping.model';
import { IntegrationVendor } from '../models/integration/integration-vendor.enum';
import { VENDOR_DEFINITIONS } from '../models/integration/vendor-definitions.constant';
import { IntegrationState } from '../reducer/integration.reducer';
import {
    selectPendingSetup,
    selectSharepointConnection,
} from '../selectors/integration.selectors';
import { IntegrationService } from '../services/integration.service';
import { autoMatchByName } from '../utils/auto-match.util';
import { getVendorAdapter } from '../vendors/vendor-adapter';

/**
 * REV-21119 E2 — the localized text for each Accounto refusal aumico-be answers with 422 `{ errorCode }`.
 * The refusals used to be 403s, and the users lib's AuthHandler logs out on every 403; as 422 the user stays
 * signed in and reads why. Any other code falls back to `accounto_import_failed_description`.
 * The ABCP sync and the closing launch show the backend's `messageKey` instead (QRM A2), which names the same keys.
 * CONNECTION_ERROR means the stored connection is not CONNECTED (e.g. after a refused key), not a network outage.
 */
export const ACCOUNTO_IMPORT_ERROR_KEYS: Readonly<Record<string, string>> = {
    KEY_EXPIRED: 'accounto_import_key_expired_description',
    INVALID_KEY: 'accounto_import_invalid_key_description',
    CONNECTION_ERROR: 'accounto_import_connection_error_description',
    TENANT_NOT_ALLOWED: 'accounto_import_tenant_not_allowed_description',
    VENDOR_MISMATCH: 'accounto_import_vendor_mismatch_description',
    TENANT_NOT_MAPPED: 'accounto_import_tenant_not_mapped_description',
    MAPPING_NOT_ACTIVE: 'accounto_import_mapping_not_active_description',
    ACCOUNTO_UNBALANCED_BOOKING: 'accounto_import_unbalanced_booking_description',
    ACCOUNTO_UNKNOWN_ACCOUNTING_TYPE: 'accounto_import_unknown_accounting_type_description',
};

function accountoImportErrorKey(errorCode: string | undefined): string {
    return errorCode !== undefined && Object.prototype.hasOwnProperty.call(ACCOUNTO_IMPORT_ERROR_KEYS, errorCode)
        ? ACCOUNTO_IMPORT_ERROR_KEYS[errorCode]
        : 'accounto_import_failed_description';
}

const CONSENT_POPUP_NAME = 'd365bc-consent';
const CONSENT_POPUP_FEATURES = 'width=600,height=700';

/** Must match the BE's BroadcastChannel name in `d365bc-consent.controller.ts`. */
const CONSENT_BROADCAST_CHANNEL = 'd365bc-auth';
const CONSENT_SUCCESS_MESSAGE = 'd365bc-auth-success';
const CONSENT_ERROR_MESSAGE = 'd365bc-auth-error';

// ── SharePoint consent popup (REV-21679) ────────────────────────────────────
const SHAREPOINT_POPUP_NAME = 'sharepoint-consent';
const SHAREPOINT_POPUP_FEATURES = 'width=600,height=700';
/** Must match the BE's BroadcastChannel name in the SharePoint consent controller. */
const SHAREPOINT_BROADCAST_CHANNEL = 'sharepoint-auth';
const SHAREPOINT_SUCCESS_MESSAGE = 'sharepoint-auth-success';
const SHAREPOINT_ERROR_MESSAGE = 'sharepoint-auth-error';
/** Hard ceiling on how long we wait for SharePoint admin consent. */
const SHAREPOINT_CONSENT_TIMEOUT_MS = 3 * 60 * 1000;
/** How often we check whether the user closed the SharePoint consent popup. */
const POPUP_CLOSED_POLL_INTERVAL_MS = 1000;

/**
 * Polling is the source of truth: postMessage and BroadcastChannel are best-effort
 * fast paths that often fail due to COOP / cross-origin severing of `window.opener`.
 * The BE has already persisted the connection by the time the popup closes, so
 * `GET /api/integrations` will see it.
 */
const CONNECTION_POLL_FIRST_DELAY_MS = 2000;
const CONNECTION_POLL_INTERVAL_MS = 2500;
/** Hard ceiling on how long we wait in `awaiting_consent`. Admin consent rarely needs > 3min. */
const CONSENT_TIMEOUT_MS = 3 * 60 * 1000;

@Injectable()
export class IntegrationEffects {
    public constructor(
        private readonly actions$: Actions,
        private readonly integrationService: IntegrationService,
        private readonly store: Store<IntegrationState>,
        private readonly coreProvider: AumicoCoreProvider,
    ) {}

    // ── Load Connections ──
    loadConnections$ = createEffect(() =>
        this.actions$.pipe(
            ofType(integrationActions.loadConnections),
            switchMap(() =>
                this.integrationService.listConnections().pipe(
                    map((connections) => integrationActions.loadConnectionsSuccess({ connections })),
                    catchError((err: HttpErrorResponse) =>
                        of(integrationActions.loadConnectionsFailure({ error: err.message })),
                    ),
                ),
            ),
        ),
    );

    // ── Initiate Consent ───────────────────────────────────────────────────
    //
    // 1. GET /api/integrations/d365bc/connect?environmentName=...&displayName=...
    //    → BE returns Microsoft admin-consent URL
    // 2. Open URL in popup
    // 3. Wait for BroadcastChannel('d365bc-auth') OR window.message OR popup-closed
    //    (BroadcastChannel works same-origin; postMessage handles the cross-origin path)
    // 4. On success: trigger loadConnections → reducer detects the new one → step `created`
    initiateConsent$ = createEffect(() =>
        this.actions$.pipe(
            ofType(integrationActions.initiateConsent),
            mergeMap(({ environmentName, displayName }) =>
                this.integrationService.getD365bcConnectUrl(environmentName, displayName).pipe(
                    mergeMap(({ authUrl }) => this.openConsentPopupAndWatch$(authUrl)),
                    catchError((err: HttpErrorResponse) =>
                        of(
                            integrationActions.consentFailed({
                                error:
                                    err.error?.message ??
                                    err.message ??
                                    'Could not obtain consent URL from server',
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    /**
     * Opens the OAuth popup and resolves via THREE concurrent paths (whichever wins):
     *
     * 1. BroadcastChannel('d365bc-auth'): best-case, only works if FE + BE callback
     *    share an origin (rare in real deployments because the popup ends up on the BE
     *    host while the FE is on the app host).
     * 2. `window.postMessage` from popup → opener: only works if COOP/COEP doesn't
     *    sever `window.opener`. With the cross-origin redirect chain through Microsoft
     *    this often gets severed, so we can't rely on it either.
     * 3. **Polling `GET /api/integrations` every few seconds**: the source of truth.
     *    The BE has already persisted the connection by the time the popup auto-closes,
     *    so a fresh `listConnections` response will contain it. The reducer's
     *    `loadConnectionsSuccess` handler detects the new D365_BC connection while
     *    `pendingSetup.step === 'awaiting_consent'` and flips it to `created`.
     *
     * Exit conditions (all monitored by a single store subscription):
     *  - reducer leaves `awaiting_consent` (success, error, or the user clicked Cancel)
     *  - hard timeout fires (3min)
     *
     * Deliberately NOT used as an exit signal: `popup.closed`. During the Microsoft
     * redirect chain the popup can transiently appear closed (cross-origin window
     * access restrictions), which used to trip a false `consentCancelled` and reset
     * the modal mid-flow.
     */
    private openConsentPopupAndWatch$(popupUrl: string): Observable<Action> {
        return new Observable<Action>((observer) => {
            const popup = window.open(popupUrl, CONSENT_POPUP_NAME, CONSENT_POPUP_FEATURES);

            let done = false;
            let pollTimer: ReturnType<typeof setTimeout> | undefined;
            let timeoutTimer: ReturnType<typeof setTimeout> | undefined;
            let channel: BroadcastChannel | null = null;
            let storeSubscription: ReturnType<
                ReturnType<Store<IntegrationState>['select']>['subscribe']
            > | null = null;

            const cleanup = (): void => {
                if (pollTimer) clearTimeout(pollTimer);
                if (timeoutTimer) clearTimeout(timeoutTimer);
                try {
                    channel?.close();
                } catch {
                    /* already closed */
                }
                window.removeEventListener('message', onWindowMessage);
                storeSubscription?.unsubscribe();
                popup?.close();
            };

            /**
             * Terminate the effect. Pass `null` when the reducer has already moved
             * `pendingSetup` to its terminal state via another action path (e.g. polling
             * already triggered the transition through `loadConnectionsSuccess`).
             */
            const finish = (action: Action | null): void => {
                if (done) return;
                done = true;
                cleanup();
                if (action != null) observer.next(action);
                observer.complete();
            };

            const handleEventData = (data: unknown): void => {
                if (data == null || typeof data !== 'object') return;
                const payload = data as { type?: string; message?: string };
                if (payload.type === CONSENT_SUCCESS_MESSAGE) {
                    // BE has persisted the connection: emit `consentCompleted` so the
                    // `reloadAfterConsent$` effect refreshes the list; the reducer then
                    // flips pendingSetup → `created`.
                    finish(integrationActions.consentCompleted());
                } else if (payload.type === CONSENT_ERROR_MESSAGE) {
                    finish(
                        integrationActions.consentFailed({
                            error: payload.message ?? 'Consent failed',
                        }),
                    );
                }
            };

            // ── Path 1: BroadcastChannel (same-origin only) ──
            try {
                channel = new BroadcastChannel(CONSENT_BROADCAST_CHANNEL);
                channel.onmessage = (event: MessageEvent): void => handleEventData(event.data);
            } catch {
                channel = null; // unsupported (older browsers / private mode)
            }

            // ── Path 2: window.postMessage from popup → opener (cross-origin) ──
            const onWindowMessage = (event: MessageEvent): void => handleEventData(event.data);
            window.addEventListener('message', onWindowMessage);

            // ── Path 3 (authoritative): BE polling ──
            // The reducer's `loadConnectionsSuccess` handler does the success detection.
            const pollConnections = (): void => {
                if (done) return;
                observer.next(integrationActions.loadConnections());
                pollTimer = setTimeout(pollConnections, CONNECTION_POLL_INTERVAL_MS);
            };
            pollTimer = setTimeout(pollConnections, CONNECTION_POLL_FIRST_DELAY_MS);

            // ── Reducer-driven exit ──
            // Whenever pendingSetup leaves `awaiting_consent` (regardless of which path
            // got us there), we're done. This is the single observable source of truth.
            storeSubscription = this.store
                .select(selectPendingSetup)
                .pipe(filter((setup) => setup.step !== 'awaiting_consent'))
                .subscribe(() => {
                    // The reducer has already transitioned. No further action needed.
                    finish(null);
                });

            // ── Hard timeout ──
            timeoutTimer = setTimeout(() => {
                if (!done) finish(integrationActions.consentCancelled());
            }, CONSENT_TIMEOUT_MS);

            // Let the UI flip into the spinner state immediately.
            observer.next(integrationActions.consentPopupOpened());

            return (): void => {
                done = true;
                cleanup();
            };
        });
    }

    /**
     * After the consent popup signals success, refresh the connections list. The reducer
     * watches `loadConnectionsSuccess` to flip pendingSetup → `created`.
     */
    reloadAfterConsent$ = createEffect(() =>
        this.actions$.pipe(
            ofType(integrationActions.consentCompleted),
            map(() => integrationActions.loadConnections()),
        ),
    );

    // ── Abacus connect (no popup, single HTTP roundtrip) ───────────────────
    //
    // POST the four form fields, let the BE handle discovery + token + smoke
    // test atomically. On success: dispatch loadConnections so the reducer
    // picks up the new connection via its `pendingSetup.step === 'awaiting_consent'`
    // detection path (same as D365 BC) and flips to `created`. On failure:
    // surface the BE error to the modal via `abacusConnectFailure`.
    initiateAbacusConnect$ = createEffect(() =>
        this.actions$.pipe(
            ofType(integrationActions.initiateAbacusConnect),
            mergeMap(({ body }) =>
                this.integrationService.connectAbacus(body).pipe(
                    map((connection) =>
                        integrationActions.abacusConnectSuccess({ connection }),
                    ),
                    catchError((err: HttpErrorResponse) => {
                        // 409 = this server/tenant is already connected in the org.
                        // Not a real failure: leave loading, close the connect modal,
                        // reopen the manage list, show a toast (see the toast effect +
                        // overview subscriber). Do NOT emit abacusConnectFailure here,
                        // that would render the in-modal error view.
                        const isAlreadyExists =
                            err.status === 409 &&
                            err.error?.errorCode === 'CONNECTION_ALREADY_EXISTS';
                        if (isAlreadyExists) {
                            return of(integrationActions.abacusConnectAlreadyExists());
                        }
                        // Generic create returns `{ errorCode, errorMessage }` on 4xx.
                        // Fall through to `message` / generic for unexpected shapes.
                        return of(
                            integrationActions.abacusConnectFailure({
                                error:
                                    err.error?.errorMessage ??
                                    err.error?.message ??
                                    err.message ??
                                    'Abacus connect failed',
                            }),
                        );
                    }),
                ),
            ),
        ),
    );

    /**
     * Toast for the already-exists case. dispatch:false — pure side effect, the
     * state transition (leave loading) is handled by the reducer, and the modal
     * choreography by the overview subscriber. Reuses the app-wide notification
     * toast (`AumicoCoreProvider`, same one the "Gespeichert" toast uses).
     */
    abacusConnectAlreadyExistsToast$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(integrationActions.abacusConnectAlreadyExists),
                tap(() =>
                    this.coreProvider.dispatchAddNotification(
                        'aumico_integration_connect_already_exists_title',
                        'aumico_integration_connect_already_exists_description',
                        NotificationType.ERROR,
                        4000,
                    ),
                ),
            ),
        { dispatch: false },
    );

    /**
     * On Abacus connect success refresh the connections list, parallel to
     * `reloadAfterConsent$`. The reducer's `loadConnectionsSuccess` handler
     * detects the newly created connection and flips `pendingSetup.step` to
     * `created`, which triggers `triggerMappingAfterCreated$` → opens the
     * manage modal directly.
     */
    reloadAfterAbacusConnect$ = createEffect(() =>
        this.actions$.pipe(
            ofType(integrationActions.abacusConnectSuccess),
            map(() => integrationActions.loadConnections()),
        ),
    );

    // ── Topal connect (no popup, single HTTP roundtrip — mirrors Abacus) ────
    initiateTopalConnect$ = createEffect(() =>
        this.actions$.pipe(
            ofType(integrationActions.initiateTopalConnect),
            mergeMap(({ body }) =>
                this.integrationService.connectTopal(body).pipe(
                    map((connection) => integrationActions.topalConnectSuccess({ connection })),
                    catchError((err: HttpErrorResponse) => {
                        const isAlreadyExists =
                            err.status === 409 &&
                            err.error?.errorCode === 'CONNECTION_ALREADY_EXISTS';
                        if (isAlreadyExists) {
                            return of(integrationActions.topalConnectAlreadyExists());
                        }
                        return of(
                            integrationActions.topalConnectFailure({
                                error:
                                    err.error?.errorMessage ??
                                    err.error?.message ??
                                    err.message ??
                                    'Topal connect failed',
                            }),
                        );
                    }),
                ),
            ),
        ),
    );

    /** Already-exists toast for Topal — mirrors the Abacus one. dispatch:false. */
    topalConnectAlreadyExistsToast$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(integrationActions.topalConnectAlreadyExists),
                tap(() =>
                    this.coreProvider.dispatchAddNotification(
                        'aumico_integration_connect_already_exists_title',
                        'aumico_integration_connect_already_exists_description',
                        NotificationType.ERROR,
                        4000,
                    ),
                ),
            ),
        { dispatch: false },
    );

    /** On Topal connect success refresh the connections list (mirrors Abacus). */
    reloadAfterTopalConnect$ = createEffect(() =>
        this.actions$.pipe(
            ofType(integrationActions.topalConnectSuccess),
            map(() => integrationActions.loadConnections()),
        ),
    );

    // ── Accounto connect (no popup, single HTTP roundtrip — mirrors Topal) ──
    initiateAccountoConnect$ = createEffect(() =>
        this.actions$.pipe(
            ofType(integrationActions.initiateAccountoConnect),
            mergeMap(({ body }) =>
                this.integrationService.connectAccounto(body).pipe(
                    map((connection) => integrationActions.accountoConnectSuccess({ connection })),
                    catchError((err: HttpErrorResponse) => {
                        const isAlreadyExists =
                            err.status === 409 &&
                            err.error?.errorCode === 'CONNECTION_ALREADY_EXISTS';
                        if (isAlreadyExists) {
                            return of(integrationActions.accountoConnectAlreadyExists());
                        }
                        return of(
                            integrationActions.accountoConnectFailure({
                                error:
                                    err.error?.errorMessage ??
                                    err.error?.message ??
                                    err.message ??
                                    'Accounto connect failed',
                            }),
                        );
                    }),
                ),
            ),
        ),
    );

    /** Already-exists toast for Accounto — mirrors the Topal one. dispatch:false. */
    accountoConnectAlreadyExistsToast$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(integrationActions.accountoConnectAlreadyExists),
                tap(() =>
                    this.coreProvider.dispatchAddNotification(
                        'aumico_integration_connect_already_exists_title',
                        'aumico_integration_connect_already_exists_description',
                        NotificationType.ERROR,
                        4000,
                    ),
                ),
            ),
        { dispatch: false },
    );

    /** On Accounto connect success refresh the connections list (mirrors Topal). */
    reloadAfterAccountoConnect$ = createEffect(() =>
        this.actions$.pipe(
            ofType(integrationActions.accountoConnectSuccess),
            map(() => integrationActions.loadConnections()),
        ),
    );

    // ── Proffix connect (no popup, single HTTP roundtrip — mirrors Topal) ──
    initiateProffixConnect$ = createEffect(() =>
        this.actions$.pipe(
            ofType(integrationActions.initiateProffixConnect),
            mergeMap(({ body }) =>
                this.integrationService.connectProffix(body).pipe(
                    map((connection) => integrationActions.proffixConnectSuccess({ connection })),
                    catchError((err: HttpErrorResponse) => {
                        const isAlreadyExists =
                            err.status === 409 &&
                            err.error?.errorCode === 'CONNECTION_ALREADY_EXISTS';
                        if (isAlreadyExists) {
                            return of(integrationActions.proffixConnectAlreadyExists());
                        }
                        return of(
                            integrationActions.proffixConnectFailure({
                                error:
                                    err.error?.errorMessage ??
                                    err.error?.message ??
                                    err.message ??
                                    'PROFFIX connect failed',
                            }),
                        );
                    }),
                ),
            ),
        ),
    );

    /** Already-exists toast for Proffix — mirrors the Topal one. dispatch:false. */
    proffixConnectAlreadyExistsToast$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(integrationActions.proffixConnectAlreadyExists),
                tap(() =>
                    this.coreProvider.dispatchAddNotification(
                        'aumico_integration_connect_already_exists_title',
                        'aumico_integration_connect_already_exists_description',
                        NotificationType.ERROR,
                        4000,
                    ),
                ),
            ),
        { dispatch: false },
    );

    /** On Proffix connect success refresh the connections list (mirrors Topal). */
    reloadAfterProffixConnect$ = createEffect(() =>
        this.actions$.pipe(
            ofType(integrationActions.proffixConnectSuccess),
            map(() => integrationActions.loadConnections()),
        ),
    );

    // ── Existing-connection lifecycle ──
    testExistingConnection$ = createEffect(() =>
        this.actions$.pipe(
            ofType(integrationActions.testExistingConnection),
            mergeMap(({ connectionId }) =>
                this.integrationService.testExistingConnection(connectionId).pipe(
                    map((result) =>
                        result.ok
                            ? integrationActions.testExistingConnectionSuccess({
                                  connectionId,
                                  lastTestAt: result.lastTestAt ?? new Date(),
                              })
                            : integrationActions.testExistingConnectionFailure({
                                  connectionId,
                                  error: result.errorMessage ?? 'Test failed',
                              }),
                    ),
                    catchError((err: HttpErrorResponse) =>
                        of(
                            integrationActions.testExistingConnectionFailure({
                                connectionId,
                                error: err.message,
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    disconnect$ = createEffect(() =>
        this.actions$.pipe(
            ofType(integrationActions.disconnect),
            mergeMap(({ connectionId }) =>
                this.integrationService.disconnect(connectionId).pipe(
                    map(() => integrationActions.disconnectSuccess({ connectionId })),
                    catchError((err: HttpErrorResponse) =>
                        of(integrationActions.disconnectFailure({ connectionId, error: err.message })),
                    ),
                ),
            ),
        ),
    );

    // ── External companies for the manage modal table ────────────────────
    //
    // The BE returns IExternalCompany[] (BC side company shapes). We wrap each
    // into an ICompanyMappingSuggestion with `suggestedAumicoCompany: null` so
    // the manage modal's detail table and the single edit modal can both
    // consume a uniform shape. The current mapping (if any) is joined in by
    // the manage modal client side from the `tenantDetail` cache.
    loadExternalCompanies$ = createEffect(() =>
        this.actions$.pipe(
            ofType(integrationActions.loadExternalCompanies),
            switchMap(({ connectionId }) =>
                this.integrationService.listExternalCompanies(connectionId).pipe(
                    map((companies) => {
                        const suggestions = companies.map((c) => ({
                            vendorCompany: {
                                externalCompanyId: c.id,
                                externalCompanyName: c.displayName || c.name,
                            },
                            suggestedAumicoCompany: null,
                        }));
                        return integrationActions.loadExternalCompaniesSuccess({
                            connectionId,
                            suggestions,
                        });
                    }),
                    catchError((err: HttpErrorResponse) =>
                        of(integrationActions.loadExternalCompaniesFailure({ error: err.message })),
                    ),
                ),
            ),
        ),
    );

    /**
     * After loadConnectionsSuccess, the reducer flips `pendingSetup.step` to `created` and
     * sets `createdConnectionId` IF we were mid-setup. Use that to gate the post Connect
     * orchestration: so this only fires after a fresh Connect, never on plain page loads.
     *
     * Two actions emitted:
     *   1. `autoMatchTrigger`: kicks off the matcher + bulk upsert effect chain
     *   2. `connectionJustCreated`: signals the integrations overview component to
     *      auto open the manage modal into the detail view for this connection
     *
     * THIS IS THE ONLY DISPATCH POINT FOR `autoMatchTrigger`: by construction
     * the auto match runs exactly once per new connection.
     */
    triggerMappingAfterCreated$ = createEffect(() =>
        this.actions$.pipe(
            ofType(integrationActions.loadConnectionsSuccess),
            withLatestFrom(this.store.select(selectPendingSetup)),
            filter(
                ([_, pendingSetup]) =>
                    pendingSetup.step === 'created' &&
                    pendingSetup.createdConnectionId != null &&
                    pendingSetup.vendor != null,
            ),
            mergeMap(([_, pendingSetup]) => {
                const connectionId = pendingSetup.createdConnectionId as string;
                const vendor = pendingSetup.vendor as IntegrationVendor;
                const actions: Action[] = [
                    integrationActions.connectionJustCreated({ connectionId, vendor }),
                ];
                // Only dispatch auto match for vendors that actually support
                // enumeration of external companies. Abacus has no Mandant
                // listing API so the matcher would just throw on the missing
                // vendor adapter. Capability flag lives on VendorDefinition.
                const def = VENDOR_DEFINITIONS.find((v) => v.vendor === vendor);
                if (def?.supportsAutoMatch === true) {
                    actions.unshift(
                        integrationActions.autoMatchTrigger({ connectionId, vendor }),
                    );
                }
                return of(...actions);
            }),
        ),
    );

    // ── Mapping CRUD (per Company) ─────────────────────────────────────────

    /** Direct upsert path (single edit row click, "+ map this Aumico co", etc). */
    upsertMapping$ = createEffect(() =>
        this.actions$.pipe(
            ofType(integrationActions.upsertMapping),
            mergeMap(({ companyId, body }) =>
                this.integrationService.upsertMapping(companyId, body).pipe(
                    map((mapping) =>
                        integrationActions.upsertMappingSuccess({ companyId, mapping }),
                    ),
                    catchError((err: HttpErrorResponse) =>
                        of(
                            integrationActions.upsertMappingFailure({
                                companyId,
                                externalCompanyId: body.externalCompanyId,
                                error: err.error?.message ?? err.message ?? 'Upsert failed',
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    /**
     * After a direct upsert succeeds, reload the tenant detail list so the
     * server remains authoritative for ordering / status. The reducer's
     * optimistic patch handles the in between state.
     */
    upsertMappingRefresh$ = createEffect(() =>
        this.actions$.pipe(
            ofType(integrationActions.upsertMappingSuccess),
            map(({ mapping }) =>
                integrationActions.loadMappedCompanies({ connectionId: mapping.connectionId }),
            ),
        ),
    );

    deleteMapping$ = createEffect(() =>
        this.actions$.pipe(
            ofType(integrationActions.deleteMapping),
            mergeMap(({ companyId, vendor }) =>
                this.integrationService.deleteMapping(companyId, vendor).pipe(
                    map(() =>
                        integrationActions.deleteMappingSuccess({ companyId, vendor }),
                    ),
                    catchError((err: HttpErrorResponse) =>
                        of(
                            integrationActions.deleteMappingFailure({
                                companyId,
                                error: err.error?.message ?? err.message ?? 'Delete failed',
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    // ── Tenant detail (D2) ─────────────────────────────────────────────────

    loadMappedCompanies$ = createEffect(() =>
        this.actions$.pipe(
            ofType(integrationActions.loadMappedCompanies),
            switchMap(({ connectionId }) =>
                this.integrationService.getCompaniesByConnection(connectionId).pipe(
                    map((companies) =>
                        integrationActions.loadMappedCompaniesSuccess({
                            connectionId,
                            companies,
                        }),
                    ),
                    catchError((err: HttpErrorResponse) =>
                        of(
                            integrationActions.loadMappedCompaniesFailure({
                                error: err.error?.message ?? err.message,
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    // ── Auto match (one shot, fail hard) ───────────────────────────────────
    //
    // Orchestration:
    //   1. fetch external companies via vendor adapter
    //   2. fetch Aumico Companies with inline mappings
    //   3. derive `existingMappedExternalIds` and `globallyMappedAumicoIds`
    //   4. run pure matcher
    //   5. POST bulk upsert with toCreate
    //   6. on success → `autoMatchPersisted` + reload tenantDetail
    //   on any HTTP failure → `autoMatchFailed` (no retry, per spec Option 1)
    //
    // Triggered exactly once per new connection by `triggerMappingAfterCreated$`.
    // No other dispatch path exists: this is "first time only" by architecture,
    // not by runtime check.
    autoMatch$ = createEffect(() =>
        this.actions$.pipe(
            ofType(integrationActions.autoMatchTrigger),
            mergeMap(({ connectionId, vendor }) => {
                const adapter = getVendorAdapter(vendor, this.integrationService);
                return forkJoin({
                    external: adapter.listExternalCompaniesNormalized(connectionId),
                    aumico: this.integrationService.listAumicoCompaniesWithInlineMappings(),
                }).pipe(
                    mergeMap(({ external, aumico }) => {
                        // Build the two "already mapped" sets from inline data.
                        const existingMappedExternalIds = new Set<string>();
                        const globallyMappedAumicoIds = new Set<string>();
                        for (const co of aumico) {
                            const mappings = co.integrations?.connectionMappings ?? [];
                            for (const m of mappings) {
                                if (m.vendor !== vendor) continue;
                                if (co._id != null) globallyMappedAumicoIds.add(co._id);
                                if (m.connectionId === connectionId) {
                                    existingMappedExternalIds.add(m.externalCompanyId);
                                }
                            }
                        }

                        const aumicoCompanies = aumico
                            .filter((c) => c._id != null && c.name != null)
                            .map((c) => ({ id: c._id as string, name: c.name }));

                        // Aumico name lookup for the optimistic patch payload —
                        // the bulk upsert response only carries the mapping, not
                        // the Aumico Company name. Build it once.
                        const aumicoNameById = new Map(
                            aumicoCompanies.map((c) => [c.id, c.name]),
                        );

                        const matchResult = autoMatchByName({
                            externalCompanies: external,
                            aumicoCompanies,
                            existingMappedExternalIds,
                            globallyMappedAumicoIds,
                        });

                        const notMappedCount =
                            matchResult.skipped.ambiguous.length +
                            matchResult.skipped.noMatch.length;

                        // Nothing to write: emit a "zero create" persisted
                        // event so the banner renders. No tenantDetail patch
                        // needed (no new mappings).
                        if (matchResult.toCreate.length === 0) {
                            return of(
                                integrationActions.autoMatchPersisted({
                                    connectionId,
                                    autoMatched: 0,
                                    notMapped: notMappedCount,
                                    errors: 0,
                                    successfulMappings: [],
                                }),
                            );
                        }

                        return this.integrationService
                            .bulkUpsertMappings({
                                // Narrow the wider IntegrationVendor enum to the mapping-vendor subset.
                                vendor: vendor as ConnectionMappingVendor,
                                connectionId,
                                mappings: matchResult.toCreate.map((m) => ({
                                    companyId: m.aumicoCompanyId,
                                    externalCompanyId: m.externalCompanyId,
                                    externalCompanyName: m.externalCompanyName,
                                })),
                            })
                            .pipe(
                                map((res) => {
                                    // Narrow `results[]` to only OK rows (have
                                    // `mapping`) and decorate with the Aumico
                                    // Co name from our local lookup. The reducer
                                    // uses this to patch tenantDetail in place.
                                    type OkResult = Extract<
                                        IBulkUpsertMappingResult,
                                        { status: 'ok' }
                                    >;
                                    const successfulMappings = res.results
                                        .filter((r): r is OkResult => r.status === 'ok')
                                        .map((r) => ({
                                            aumicoCompanyId: r.companyId,
                                            aumicoCompanyName:
                                                aumicoNameById.get(r.companyId) ?? '',
                                            mapping: r.mapping,
                                        }));
                                    return integrationActions.autoMatchPersisted({
                                        connectionId,
                                        autoMatched: res.summary.ok,
                                        notMapped: notMappedCount,
                                        errors: res.summary.errors,
                                        successfulMappings,
                                    });
                                }),
                            );
                    }),
                    catchError((err: HttpErrorResponse) =>
                        of(
                            integrationActions.autoMatchFailed({
                                connectionId,
                                error: err.error?.message ?? err.message ?? 'Auto Match failed',
                            }),
                        ),
                    ),
                );
            }),
        ),
    );

    // ── D365 BC journal import ─────────────────────────────────────────────

    /** Load the BC fiscal years for an active mapping. switchMap: a tenant change cancels in flight. */
    loadD365BcFiscalYears$ = createEffect(() =>
        this.actions$.pipe(
            ofType(integrationActions.d365bcFiscalYearsLoad),
            switchMap(({ connectionId, externalCompanyId }) =>
                this.integrationService
                    .listD365BcFiscalYears(connectionId, externalCompanyId)
                    .pipe(
                        map((fiscalYears) =>
                            integrationActions.d365bcFiscalYearsLoadSuccess({ fiscalYears }),
                        ),
                        catchError((err: HttpErrorResponse) =>
                            of(
                                integrationActions.d365bcFiscalYearsLoadFailure({
                                    error: err.error?.message ?? err.message,
                                }),
                            ),
                        ),
                    ),
            ),
        ),
    );

    /**
     * Run the import. switchMap so a quick second click cancels the prior one
     * (rare but defensive). The page subscribes to importStatus and renders
     * the loading modal while we're in flight, the summary modal on success.
     */
    runD365BcImport$ = createEffect(() =>
        this.actions$.pipe(
            ofType(integrationActions.d365bcJournalImportRun),
            switchMap(({ body }) =>
                this.integrationService.importD365BcJournal(body).pipe(
                    map((result) => integrationActions.d365bcJournalImportSuccess({ result })),
                    catchError((err: HttpErrorResponse) =>
                        of(
                            integrationActions.d365bcJournalImportFailure({
                                error:
                                    err.error?.message ??
                                    err.message ??
                                    'D365 BC journal import failed',
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    /**
     * Abacus journal import. Same shape as `runD365BcImport$`. Generic create
     * style error envelope (`{ errorCode, errorMessage }`), so we read
     * `errorMessage` first like the abacus connect effect.
     */
    runAbacusImport$ = createEffect(() =>
        this.actions$.pipe(
            ofType(integrationActions.abacusJournalImportRun),
            switchMap(({ body }) =>
                this.integrationService.importAbacusJournal(body).pipe(
                    map((result) => integrationActions.abacusJournalImportSuccess({ result })),
                    catchError((err: HttpErrorResponse) =>
                        of(
                            integrationActions.abacusJournalImportFailure({
                                error:
                                    err.error?.errorMessage ??
                                    err.error?.message ??
                                    err.message ??
                                    'Abacus journal import failed',
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    /** Topal journal import. Same shape as `runAbacusImport$` (generic create error envelope). */
    runTopalImport$ = createEffect(() =>
        this.actions$.pipe(
            ofType(integrationActions.topalJournalImportRun),
            switchMap(({ body }) =>
                this.integrationService.importTopalJournal(body).pipe(
                    map((result) => integrationActions.topalJournalImportSuccess({ result })),
                    catchError((err: HttpErrorResponse) =>
                        of(
                            integrationActions.topalJournalImportFailure({
                                error:
                                    err.error?.errorMessage ??
                                    err.error?.message ??
                                    err.message ??
                                    'Topal journal import failed',
                                status: err.status,
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    /**
     * User-facing, localized error toast for a failed Topal import. The raw BE error (e.g.
     * "No Topal fiscal year overlaps 2025-01-01..2025-12-31") is technical + English, so the user
     * never sees it. A 404 means the report's period has no matching Topal fiscal year — the common
     * "wrong dates" case — which gets a specific, actionable message; anything else gets a generic
     * one. Reuses the same notification component as the connect-error toasts (consistent styling).
     */
    notifyTopalImportFailure$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(integrationActions.topalJournalImportFailure),
                tap(({ status }) =>
                    this.coreProvider.dispatchAddNotification(
                        'topal_import_failed_title',
                        status === 404
                            ? 'topal_import_no_fiscal_year_description'
                            : 'topal_import_failed_description',
                        NotificationType.ERROR,
                        7000,
                    ),
                ),
            ),
        { dispatch: false },
    );

    /** Accounto journal import. Same shape as `runTopalImport$` (generic create error envelope). */
    runAccountoImport$ = createEffect(() =>
        this.actions$.pipe(
            ofType(integrationActions.accountoJournalImportRun),
            switchMap(({ body }) =>
                this.integrationService.importAccountoJournal(body).pipe(
                    map((result) => integrationActions.accountoJournalImportSuccess({ result })),
                    catchError((err: HttpErrorResponse) =>
                        of(
                            integrationActions.accountoJournalImportFailure({
                                error:
                                    err.error?.errorMessage ??
                                    err.error?.message ??
                                    err.message ??
                                    'Accounto journal import failed',
                                status: err.status,
                                errorCode: err.error?.errorCode,
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    /**
     * User-facing, localized error toast for a failed Accounto import. The raw BE error is
     * technical + English, so the user never sees it. Like PROFFIX there is no "no matching
     * fiscal year" case (the BE reads the report's date range directly). An expired API key
     * (`KEY_EXPIRED`, REV-21119 D7) and each 422 refusal of E2 get their own text
     * (`ACCOUNTO_IMPORT_ERROR_KEYS`).
     */
    notifyAccountoImportFailure$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(integrationActions.accountoJournalImportFailure),
                tap(({ errorCode }) =>
                    this.coreProvider.dispatchAddNotification(
                        'accounto_import_failed_title',
                        accountoImportErrorKey(errorCode),
                        NotificationType.ERROR,
                        7000,
                    ),
                ),
            ),
        { dispatch: false },
    );

    /** Proffix journal import. Same shape as `runTopalImport$` (generic create error envelope). */
    runProffixImport$ = createEffect(() =>
        this.actions$.pipe(
            ofType(integrationActions.proffixJournalImportRun),
            switchMap(({ body }) =>
                this.integrationService.importProffixJournal(body).pipe(
                    map((result) => integrationActions.proffixJournalImportSuccess({ result })),
                    catchError((err: HttpErrorResponse) =>
                        of(
                            integrationActions.proffixJournalImportFailure({
                                error:
                                    err.error?.errorMessage ??
                                    err.error?.message ??
                                    err.message ??
                                    'Proffix journal import failed',
                                status: err.status,
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    /**
     * User-facing, localized error toast for a failed Proffix import. The raw BE error is
     * technical + English, so the user never sees it. Unlike Topal there is no "no matching
     * fiscal year" case: the BE filters the journal by the report's dates directly, so an
     * empty period just yields zero rows. Every failure gets the generic message.
     */
    notifyProffixImportFailure$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(integrationActions.proffixJournalImportFailure),
                tap(() =>
                    this.coreProvider.dispatchAddNotification(
                        'proffix_import_failed_title',
                        'proffix_import_failed_description',
                        NotificationType.ERROR,
                        7000,
                    ),
                ),
            ),
        { dispatch: false },
    );

    // ── SharePoint document source (REV-21679) ──────────────────────────────
    //
    // Connect mirrors the D365 BC admin-consent popup machinery: GET the consent
    // URL, open the popup, then resolve via POLLING the project's SharePoint
    // connection (the source of truth) with BroadcastChannel('sharepoint-auth') /
    // window.postMessage as best-effort fast paths, plus popup-closed and a hard
    // timeout as give-up signals. On success we dispatch
    // `sharepointConnectCompleted`, which reloads the connection AND the folders
    // so the pure view flips to `connected` without a manual page refresh.
    /**
     * REV-21860 — SharePoint connect from the ORG SETTINGS integration card.
     *
     * Deliberately reuses the GENERIC `openConsentPopupAndWatch$` rather than the
     * closing-scoped `openSharepointConsentPopupAndWatch$` below, because at org level
     * the resolution signal we want is exactly the generic one: poll
     * `GET /api/integrations` and let the reducer's vendor-agnostic detection in
     * `loadConnectionsSuccess` spot the new SHAREPOINT connection and move
     * `pendingSetup` to `created`. The closing-scoped watcher instead polls
     * `/projects/:id/sharepoint-connection`, which needs a projectId this surface has not
     * got (and the BE does not need — see `getSharepointConnectUrl`).
     *
     * Caveat, deliberate: the generic watcher's BroadcastChannel/postMessage fast paths
     * listen for the `d365bc-auth*` message names, so a SharePoint callback does not
     * trigger them. That costs nothing — both are documented as best-effort only and
     * CANNOT work in the real deployment anyway (the app and the BE callback are on
     * different origins). The authoritative path is the poll, which settles within one
     * `CONNECTION_POLL_INTERVAL_MS` tick.
     */
    sharepointOrgConnect$ = createEffect(() =>
        this.actions$.pipe(
            ofType(integrationActions.sharepointOrgConnectInitiate),
            mergeMap(({ displayName }) =>
                // No projectId: the BE mints the connection from the caller's org.
                this.integrationService.getSharepointConnectUrl(null, displayName).pipe(
                    mergeMap(({ authUrl }) => this.openConsentPopupAndWatch$(authUrl)),
                    catchError((err: HttpErrorResponse) =>
                        of(
                            integrationActions.consentFailed({
                                error:
                                    err.error?.message ??
                                    err.message ??
                                    'Could not obtain SharePoint consent URL from server',
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    sharepointConnect$ = createEffect(() =>
        this.actions$.pipe(
            ofType(integrationActions.sharepointConnectInitiate),
            mergeMap(({ projectId }) =>
                this.integrationService.getSharepointConnectUrl(projectId).pipe(
                    mergeMap(({ authUrl }) =>
                        this.openSharepointConsentPopupAndWatch$(authUrl, projectId),
                    ),
                    catchError((err: HttpErrorResponse) =>
                        of(
                            integrationActions.sharepointConnectFailed({
                                error:
                                    err.error?.message ??
                                    err.message ??
                                    'Could not obtain SharePoint consent URL from server',
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    /**
     * Opens the SharePoint OAuth popup and resolves via FOUR concurrent paths
     * (mirrors the proven `openConsentPopupAndWatch$` D365 BC machinery):
     *
     * 1. **Polling `GET /api/projects/:id/sharepoint-connection` every few seconds**:
     *    the SOURCE OF TRUTH. The BE has already persisted the connection by the
     *    time the popup lands on its callback, so a fresh `sharepointLoadConnection`
     *    response carries `status: 'connected'` and the reducer flips the pure view.
     * 2. BroadcastChannel('sharepoint-auth'): a best-effort fast path only. It is
     *    ORIGIN-SCOPED and therefore CANNOT work in the real deployment — the app
     *    runs on the FE origin (e.g. localhost:4230) while the popup/callback is
     *    served by the BE (e.g. localhost:3800). Kept because it costs nothing.
     * 3. `window.postMessage` from popup → opener: also best-effort (COOP/COEP and
     *    the Microsoft redirect chain frequently sever `window.opener`). The origin
     *    is deliberately NOT compared against the app origin — the popup lives on
     *    the BE host — we check `event.source === popup` where the browser lets us.
     * 4. `popup.closed` / hard timeout (3 min) as the give-up signals.
     *
     * Exit conditions (any of them clears the poll timer via `cleanup()`):
     *  - the store reports the SharePoint connection as `connected` → emit
     *    `sharepointConnectCompleted({ projectId })`, which refreshes the connection
     *    AND the folder list so the connected UI appears without a manual reload
     *  - an explicit error message arrives
     *  - the user closed the popup
     *  - the hard timeout fires
     *  - the observable is unsubscribed (teardown runs `cleanup()`)
     */
    private openSharepointConsentPopupAndWatch$(
        popupUrl: string,
        projectId: string,
    ): Observable<Action> {
        return new Observable<Action>((observer) => {
            const popup = window.open(popupUrl, SHAREPOINT_POPUP_NAME, SHAREPOINT_POPUP_FEATURES);

            let done = false;
            let pollTimer: ReturnType<typeof setTimeout> | undefined;
            let timeoutTimer: ReturnType<typeof setTimeout> | undefined;
            let closedTimer: ReturnType<typeof setTimeout> | undefined;
            let channel: BroadcastChannel | null = null;
            let storeSubscription: ReturnType<
                ReturnType<Store<IntegrationState>['select']>['subscribe']
            > | null = null;

            const cleanup = (): void => {
                if (pollTimer) clearTimeout(pollTimer);
                if (timeoutTimer) clearTimeout(timeoutTimer);
                if (closedTimer) clearTimeout(closedTimer);
                pollTimer = undefined;
                timeoutTimer = undefined;
                closedTimer = undefined;
                try {
                    channel?.close();
                } catch {
                    /* already closed */
                }
                window.removeEventListener('message', onWindowMessage);
                storeSubscription?.unsubscribe();
                try {
                    popup?.close();
                } catch {
                    /* cross-origin popup already gone */
                }
            };

            /**
             * Terminate the effect. Pass `null` when no further action is needed
             * (e.g. the reducer already reached the terminal state on its own).
             */
            const finish = (action: Action | null): void => {
                if (done) return;
                done = true;
                cleanup();
                if (action != null) observer.next(action);
                observer.complete();
            };

            const handleEventData = (data: unknown): void => {
                if (data == null || typeof data !== 'object') return;
                const payload = data as { type?: string; message?: string };
                if (payload.type === SHAREPOINT_SUCCESS_MESSAGE) {
                    finish(integrationActions.sharepointConnectCompleted({ projectId }));
                } else if (payload.type === SHAREPOINT_ERROR_MESSAGE) {
                    finish(
                        integrationActions.sharepointConnectFailed({
                            error: payload.message ?? 'SharePoint consent failed',
                            projectId,
                        }),
                    );
                }
            };

            // ── Path 2: BroadcastChannel (same-origin only — see doc comment) ──
            try {
                channel = new BroadcastChannel(SHAREPOINT_BROADCAST_CHANNEL);
                channel.onmessage = (event: MessageEvent): void => handleEventData(event.data);
            } catch {
                channel = null;
            }

            // ── Path 3: window.postMessage from popup → opener (cross-origin) ──
            // NO `event.origin` equality check: the popup is served by the BE, so its
            // origin differs from the app's by design. Where the browser still exposes
            // `event.source` we use it to make sure the message came from OUR popup.
            const onWindowMessage = (event: MessageEvent): void => {
                if (popup != null && event.source != null && event.source !== popup) return;
                handleEventData(event.data);
            };
            window.addEventListener('message', onWindowMessage);

            // ── Path 1 (authoritative): BE polling ──
            // Each tick re-dispatches the connection load; `sharepointLoadConnectionSuccess`
            // replaces the slice's connection, so the store subscription below sees
            // `connected` as soon as the BE has persisted it.
            const pollConnection = (): void => {
                if (done) return;
                observer.next(integrationActions.sharepointLoadConnection({ projectId }));
                pollTimer = setTimeout(pollConnection, CONNECTION_POLL_INTERVAL_MS);
            };
            pollTimer = setTimeout(pollConnection, CONNECTION_POLL_FIRST_DELAY_MS);

            // ── Store-driven exit: the connection went `connected` ──
            const connectionSubscription = this.store
                .select(selectSharepointConnection)
                .pipe(filter((connection) => connection?.status === 'connected'))
                .subscribe(() => {
                    // Refreshes connection + folders (see `reloadSharepointAfterConnect$`).
                    finish(integrationActions.sharepointConnectCompleted({ projectId }));
                });
            storeSubscription = connectionSubscription;
            // The store is a BehaviorSubject-like: if it was ALREADY connected the
            // callback above ran synchronously, before `storeSubscription` was assigned,
            // so `cleanup()` could not have unsubscribed it. Close that hole here.
            if (done) connectionSubscription.unsubscribe();

            // ── The user closed the popup → stop polling ──
            const watchClosed = (): void => {
                if (done) return;
                let closed = false;
                try {
                    closed = popup?.closed === true;
                } catch {
                    closed = false; // cross-origin access denied — keep waiting
                }
                if (closed) {
                    // One last read of the truth before giving up: the BE may have
                    // persisted the connection just before the popup self-closed.
                    observer.next(integrationActions.sharepointLoadConnection({ projectId }));
                    finish(null);
                    return;
                }
                closedTimer = setTimeout(watchClosed, POPUP_CLOSED_POLL_INTERVAL_MS);
            };
            closedTimer = setTimeout(watchClosed, POPUP_CLOSED_POLL_INTERVAL_MS);

            // ── Hard timeout ──
            timeoutTimer = setTimeout(() => {
                finish(
                    integrationActions.sharepointConnectFailed({
                        error: 'SharePoint consent timed out',
                        projectId,
                    }),
                );
            }, SHAREPOINT_CONSENT_TIMEOUT_MS);

            // Flip the pure view into its "connecting" state immediately.
            observer.next(integrationActions.sharepointConnectPopupOpened());

            return (): void => {
                done = true;
                cleanup();
            };
        });
    }

    /**
     * After the SharePoint popup signals success, reload BOTH the connection and the
     * folder list for the project — the connected UI (banner + "Weiteren Ordner
     * hinzufügen") must appear without a manual page refresh.
     */
    reloadSharepointAfterConnect$ = createEffect(() =>
        this.actions$.pipe(
            ofType(integrationActions.sharepointConnectCompleted),
            mergeMap(({ projectId }) => [
                integrationActions.sharepointLoadConnection({ projectId }),
                integrationActions.sharepointLoadFolders({ projectId }),
            ]),
        ),
    );

    /**
     * REV-21869 — a failed consent popup is not proof that nothing is connected.
     *
     * The popup reports "Diese SharePoint Verbindung besteht bereits" when the tenant already HAS a live
     * connection, and it can time out after the backend already stored one. Either way the reducer set the
     * red error state, and nothing read the connection again — so the wizard kept telling a trustee who was
     * connected that connecting had failed, until a page reload.
     *
     * One re-read. Only a `connected` answer is applied (plus the folder list, as after a successful connect):
     * any other answer — `disconnected` for a consent that really was refused — keeps the failure sentence,
     * which is then the truth. A failed re-read changes nothing either.
     */
    recheckSharepointAfterFailedConnect$ = createEffect(() =>
        this.actions$.pipe(
            ofType(integrationActions.sharepointConnectFailed),
            filter((action): action is typeof action & { projectId: string } => action.projectId != null),
            switchMap(({ projectId }) =>
                this.integrationService.getSharepointConnection(projectId).pipe(
                    filter((connection) => connection.status === 'connected'),
                    mergeMap((connection) => [
                        integrationActions.sharepointLoadConnectionSuccess({ connection }),
                        integrationActions.sharepointLoadFolders({ projectId }),
                    ]),
                    catchError(() => EMPTY),
                ),
            ),
        ),
    );

    sharepointLoadConnection$ = createEffect(() =>
        this.actions$.pipe(
            ofType(integrationActions.sharepointLoadConnection),
            switchMap(({ projectId }) =>
                this.integrationService.getSharepointConnection(projectId).pipe(
                    map((connection) =>
                        integrationActions.sharepointLoadConnectionSuccess({ connection }),
                    ),
                    catchError((err: HttpErrorResponse) =>
                        of(
                            integrationActions.sharepointLoadConnectionFailure({
                                error: err.error?.message ?? err.message,
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    sharepointLoadFolders$ = createEffect(() =>
        this.actions$.pipe(
            ofType(integrationActions.sharepointLoadFolders),
            switchMap(({ projectId }) =>
                this.integrationService.listSharepointFolders(projectId).pipe(
                    map((folders) => integrationActions.sharepointLoadFoldersSuccess({ folders })),
                    catchError((err: HttpErrorResponse) =>
                        of(
                            integrationActions.sharepointLoadFoldersFailure({
                                error: err.error?.message ?? err.message,
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    sharepointAddFolders$ = createEffect(() =>
        this.actions$.pipe(
            ofType(integrationActions.sharepointAddFolders),
            mergeMap(({ projectId, connectionId, folders }) =>
                this.integrationService.addSharepointFolders(projectId, connectionId, folders).pipe(
                    map((updated) =>
                        integrationActions.sharepointAddFoldersSuccess({
                            projectId,
                            folders: updated,
                        }),
                    ),
                    catchError((err: HttpErrorResponse) =>
                        of(
                            integrationActions.sharepointAddFoldersFailure({
                                error: err.error?.message ?? err.message,
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    /**
     * REV-21057 BUG 3 — after a successful add, re-read the AUTHORITATIVE list.
     *
     * The POST only returns the newly-created bindings (the BE dedupes by drive + folder
     * item), so the reducer's merge alone can never surface server-side enrichment or a
     * concurrent change from another tab. The GET is the single source of truth for what the
     * project has bound; the reducer merge keeps the UI correct in the meantime.
     */
    reloadSharepointFoldersAfterAdd$ = createEffect(() =>
        this.actions$.pipe(
            ofType(integrationActions.sharepointAddFoldersSuccess),
            map(({ projectId }) => integrationActions.sharepointLoadFolders({ projectId })),
        ),
    );

    sharepointUpdateFolder$ = createEffect(() =>
        this.actions$.pipe(
            ofType(integrationActions.sharepointUpdateFolder),
            mergeMap(({ projectId, folderId, includeSubfolders }) =>
                this.integrationService.updateSharepointFolder(projectId, folderId, includeSubfolders).pipe(
                    map(() => integrationActions.sharepointUpdateFolderSuccess({ folderId, includeSubfolders })),
                    catchError((err: HttpErrorResponse) =>
                        of(
                            integrationActions.sharepointUpdateFolderFailure({
                                folderId,
                                // The value to roll BACK to — the opposite of what we tried.
                                includeSubfolders: !includeSubfolders,
                                error: err.error?.message ?? err.message,
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    sharepointRemoveFolder$ = createEffect(() =>
        this.actions$.pipe(
            ofType(integrationActions.sharepointRemoveFolder),
            mergeMap(({ projectId, folderId }) =>
                this.integrationService.removeSharepointFolder(projectId, folderId).pipe(
                    map(() => integrationActions.sharepointRemoveFolderSuccess({ folderId })),
                    catchError((err: HttpErrorResponse) =>
                        of(
                            integrationActions.sharepointRemoveFolderFailure({
                                error: err.error?.message ?? err.message,
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    sharepointLoadPickerToken$ = createEffect(() =>
        this.actions$.pipe(
            ofType(integrationActions.sharepointLoadPickerToken),
            mergeMap(({ connectionId }) =>
                this.integrationService.getSharepointPickerToken(connectionId).pipe(
                    map(({ token, baseUrl }) =>
                        integrationActions.sharepointLoadPickerTokenSuccess({
                            token,
                            baseUrl: baseUrl ?? null,
                        }),
                    ),
                    catchError((err: HttpErrorResponse) =>
                        of(
                            integrationActions.sharepointLoadPickerTokenFailure({
                                error: err.error?.message ?? err.message,
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );
}
